import React, { useEffect, useState, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import axios from "axios";

import MainTemplate from "@/components/Templates/MainTemplate";
import Testimonials from "@/components/Organisms/Testimonials";
import PartnerForm from "@/components/Organisms/PartnerForm";
import HeroSection from "./HeroSection";
import HowItWorksSection from "./HowItWorksSection";
import HealthConditionsSection from "./HealthConditionsSection";
import AllPackagesSection from "./AllPackagesSection";

import { useAppSelector } from "@/hooks";
import {
  generateUrlArguments,
  isMobile,
  renderMetaTags,
  timezone as tz,
} from "@/utils";
import { DEFAULT_URL_ARGUMENTS } from "@/constants";

import mapPartnershipData from "./mapPartnershipData";
import { useRouter } from "next/router";

const PartnershipPage = ({ slug }) => {
  let urlParams = "";
  if (typeof window !== "undefined") {
    urlParams = window.location.search;
  }

  const { data, isLoading, success, error } =
    useAppSelector((state) => state.healthProgramV1) || {};

  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [planData, setPlanData] = useState([]);

  const { hero, howItWorks, forms, healthConditions, testimonials } =
    useMemo(() => {
      if (!Object.keys(data)?.length) return {};
      return mapPartnershipData(data);
    }, [data]);

  const isNutritionHomePage = slug === "nutrition";

  const isVistaraPage = slug?.includes("vistara") || false;
  const isPazCareYogaPage = slug === "pazcare-yoga-program";
  const isJellybeanYogaPage = slug === "jellybean-yoga-program";
  const isJellybeanNutritionPage = slug === "jellybean-nutrition-program";
  const isBainYogaPage = slug === "bain-yoga-program";
  const isMyGatePage = slug === "mygate-partnership";
  const isNoBrokerHoodPage = slug === "nobrokerhood-partnership";
  const isSmyttenYogaPage = slug === "smytten-yoga";
  const isIndusindYogaPage = slug === "indusind-yoga-program";
  const isDrMorePenPage = slug === "dr-morepen";
  const isPazCareNutritionPage = slug === "pazcare-nutrition-program";
  const isJellybeanPartnerPage =
    slug === (isJellybeanYogaPage || isJellybeanNutritionPage);
  const isBainNutritionPage = slug === "bain-nutrition-program";
  const isSmyttenNutritionPage = slug === "smytten-nutrition";
  const isIndusIndNutritionPage = slug === "induslndbank-nutrition";
  const isVisaPage = [
    "visa",
    "visa-nutrition",
    "visa-yogapackage",
    "visa-nutritionpackage",
    "visa-yogatrial",
  ].includes(slug);
  const isVisaNutritionPage = slug === "visa-nutrition";

  const timezone = forms?.defaultTimezone || tz;
  const pageType = forms?.pageType;
  const showAllPackages = forms?.showAllPackages;
  const isMainWebsitePage = forms?.isMainWebsitePage;
  const defaultUrlArguments = forms?.defaultUrlArguments;

  const isPartnershipPage = !forms?.showHeader;

  useEffect(() => {
    const productTypeMap = {
      YOGA_PARTNERSHIP: "ZOOM_YOGA",
      NUTRITION_PARTNERSHIP: "NUTRITION",
      HEALTH_PARTNERSHIP: "HEALTH_PROGRAM",
      DERMA_PARTNERSHIP: "DERMA",
    };

    const packagesUrl = `${
      process.env.NEXT_PUBLIC_LOCAL_API_URL
    }api/v1/package-pricings/?product_type=${productTypeMap[pageType]}${
      isPartnershipPage ? `&page_type=${pageType}` : ""
    }&timezone=${timezone}`;

    if (showAllPackages) {
      axios.get(packagesUrl).then((res) => {
        const _planData =
          res?.data?.data?.pricing?.filter((pac) =>
            [
              "nutrition-monthly-pack",
              "nutrition-quarterly-pack",
              "nutrition-half-yearly-pack",
              "nutrition-yearly-pack",
            ].includes(pac?.slug)
          ) || [];
        setPlanData(_planData);
      });
    }
  }, [timezone, showAllPackages, pageType]);

  let urlsArguments =
    generateUrlArguments(
      urlParams,
      isMainWebsitePage
        ? {
            lead_via_main_website: true,
            ...(defaultUrlArguments || {}),
          }
        : {
            ...(forms?.defaultAdsetName && {
              adset_name: forms?.defaultAdsetName,
            }),
            lead_via_partner_website: true,
            ...(defaultUrlArguments || {}),
          }
    ) || DEFAULT_URL_ARGUMENTS;

  if (data?.hero?.acceptCVId) {
    urlsArguments = generateUrlArguments(urlParams, {
      lead_via_vistara: true,
      lead_via_partner_website: true,
      adset_name: "Vistara",
    });
  }

  if (isDrMorePenPage) {
    urlsArguments = generateUrlArguments(urlParams, {
      lead_via_partner_website: true,
      adset_name: "drmorepen-referrals",
    });
  }

  if (isPazCareYogaPage || isPazCareNutritionPage) {
    urlsArguments = generateUrlArguments(urlParams, {
      lead_via_partner_website: true,
      adset_name: "Pazcare",
    });
  }

  if (isJellybeanPartnerPage) {
    urlsArguments = generateUrlArguments(urlParams, {
      lead_via_partner_website: true,
      adset_name: "Jellybean",
    });
  }

  if (isBainYogaPage || isBainNutritionPage) {
    urlsArguments = generateUrlArguments(urlParams, {
      lead_via_bain: true,
      lead_via_partner_website: true,
      adset_name: "bain_corporate",
    });
  }

  if (isMyGatePage) {
    urlsArguments = generateUrlArguments(urlParams, {
      lead_via_mygate: true,
      lead_via_partner_website: true,
      adset_name: "MyGate",
    });
  }

  if (isNoBrokerHoodPage) {
    urlsArguments = generateUrlArguments(urlParams, {
      lead_via_partner_website: true,
      adset_name: "nobrokerhood",
    });
  }

  if (isSmyttenYogaPage || isSmyttenNutritionPage) {
    urlsArguments = generateUrlArguments(urlParams, {
      lead_via_partner_website: true,
      adset_name: "Smytten_referral",
    });
  }
  if (isIndusindYogaPage || isIndusIndNutritionPage) {
    urlsArguments = generateUrlArguments(urlParams, {
      lead_via_partner_website: true,
      adset_name: "indusind",
    });
  }
  if (isVisaPage || isVisaNutritionPage) {
    urlsArguments = generateUrlArguments(urlParams, {
      lead_via_partner_website: true,
    });
  }

  const FormComponent = (isCallbackModal) => (
    <PartnerForm
      // URL Arguments
      urlParams={urlsArguments}
      // Form Data
      {...forms}
      // Show Form as Modal
      isCallbackModal={isCallbackModal}
      setShowCallbackModal={setShowCallbackModal}
      isPartnershipPage={isPartnershipPage}
      paymentSuccessPath={
        slug === "nutrition"
          ? "trial/nutrition/payments/success"
            ? slug === "3-week-wellness-bootcamp"
            : "trial/health/payments/success"
          : "payments/success"
      }
    />
  );

  const onHandleRequestCallback = () => setShowCallbackModal(true);

  const starterPack = planData?.[0];
  const ctaTitle =
    starterPack && hero?.ctaTitle
      ? `Packages starting from ${starterPack?.currency} ${starterPack?.price}`
      : hero?.ctaTitle;

  const getContentStyle = (type) => {
    switch (type) {
      case "YOGA_PARTNERSHIP":
        return {
          padding: "90px 0",
          width: "540px",
        };
      case "HEALTH_PARTNERSHIP":
        return {
          padding: "90px 0",
          width: "590px",
        };
      case "NUTRITION_PARTNERSHIP":
      case "DERMA_PARTNERSHIP":
        return {
          padding: "50px 0",
          width: "330px",
        };
      default:
        return {
          padding: "50px 0",
          width: "330px",
        };
    }
  };

  const { padding, width } = getContentStyle(pageType);

  const metaTags = renderMetaTags(data);

  return (
    <>
      {metaTags}
      <MainTemplate
        // isLoading={isLoading}
        error={error}
        hideHeader={!forms?.showHeader}
        noIndexNoFollow={isPartnershipPage}
      >
        <HeroSection
          {...hero}
          heroPadding={padding}
          contentWidth={width}
          ctaTitle={ctaTitle}
        />
        <HowItWorksSection slug={slug} {...howItWorks}>
          {FormComponent()}
        </HowItWorksSection>
        <HealthConditionsSection
          onHandleRequestCallback={onHandleRequestCallback}
          {...healthConditions}
        />
        {showAllPackages && (
          <AllPackagesSection
            urlsArguments={urlsArguments}
            isNutritionHomePage={isNutritionHomePage}
            planData={planData}
            {...forms}
          />
        )}
        {testimonials?.title && (
          <Testimonials data={testimonials} isMobile={isMobile} />
        )}
        <AnimatePresence>
          {showCallbackModal && FormComponent(true)}
        </AnimatePresence>
      </MainTemplate>
    </>
  );
};

export default PartnershipPage;
